import html2canvas from 'html2canvas';
import { useState, useCallback, useEffect, useRef } from 'react';
import { TankplanReportData, ReportGenerationOptions, ReportSubType } from 'src/utils/reportGenerator/ReportGeneratorTypes';
import { useStowageTankPlanStore } from 'src/context/StowageTankPlanContext';

export interface TankPlanReportGenerationData {
    reportSubType: ReportSubType;
    baseData: TankplanReportData;
    options: ReportGenerationOptions;
}

const CANVAS_SCALE_FACTOR = 2;
const JPEG_QUALITY = 0.1;
export const STABILITY_TIMEOUT = 300;
export const FALLBACK_PROCESS_DELAY = STABILITY_TIMEOUT + 1200; // 1.5s
const ELEMENT_POLL_INTERVAL = 100;
const ELEMENT_POLL_MAX_ATTEMPTS = 50; // 5 seconds max
const TANK_PLAN_CONTAINER_ID = 'tankPlanPdfReportContainer';
const SIGNATURE_RECHECK_INTERVAL = 50;
const SIGNATURE_RECHECK_MAX_ATTEMPTS = 40; // 2 seconds max

/**
 * Builds the same "condition|portCallGuid|berthCallGuid" signature that
 * TankPlanPdfReportContainer stamps onto the DOM (as data-report-signature) once it has
 * re-rendered for a given report. Comparing this against the live DOM attribute lets us
 * positively confirm the capture target actually reflects the report we intend to capture,
 * instead of trusting a MutationObserver + fixed timeout to have settled on the right render
 * (a mutation can be triggered by something unrelated and fire the stability timer while the DOM
 * is still showing the previous report in the queue).
 */
const getExpectedReportSignature = (report: TankPlanReportGenerationData): string => {
    const condition = report.reportSubType.tankPlanCondition ?? 'none';
    const portCallGuid = report.baseData.selectedPortCall?.portCallGuid ?? 'none';
    const berthCallGuid = report.baseData.selectedBerthCall?.berthCallGuid ?? 'none';
    return `${condition}|${portCallGuid}|${berthCallGuid}`;
};

/**
 * Custom hook for managing tank plan image generation with internal state
 */
export const useTankPlanImageGenerator = (
    updateProgressMessage: (reportSubType?: ReportSubType, index?: number, total?: number) => void,
    onError?: (message: string) => void
): {
    generateTankPlanReports: (tankPlanElement: HTMLElement, reports: TankPlanReportGenerationData[], totalNumberOfReports: number) => void;
    generatedReports: TankPlanReportGenerationData[];
    isGenerating: boolean;
} => {
    const { setReportTankPlanCondition, setReportPortCall, setReportBerthCall } = useStowageTankPlanStore();
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedReports, setGeneratedReports] = useState<TankPlanReportGenerationData[]>([]);

    // Use refs to track current generation state to avoid closure issues
    const reportsRef = useRef<TankPlanReportGenerationData[]>([]);
    const currentIndexRef = useRef(0);
    const totalReportNumberRef = useRef(0);
    const tankPlanElementRef = useRef<HTMLElement | null>(null);
    const observerRef = useRef<MutationObserver | null>(null);
    const timeoutRef = useRef<number | null>(null);
    const ensureProcessingTimeoutRef = useRef<number | null>(null);
    const signatureRecheckTimeoutRef = useRef<number | null>(null);
    const signatureRecheckAttemptsRef = useRef(0);

    const clearTimersAndObserver = useCallback(() => {
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (ensureProcessingTimeoutRef.current !== null) {
            window.clearTimeout(ensureProcessingTimeoutRef.current);
            ensureProcessingTimeoutRef.current = null;
        }
        if (signatureRecheckTimeoutRef.current !== null) {
            window.clearTimeout(signatureRecheckTimeoutRef.current);
            signatureRecheckTimeoutRef.current = null;
        }
        signatureRecheckAttemptsRef.current = 0;
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
    }, []);

    const cleanup = useCallback(() => {
        clearTimersAndObserver();
        // Always clear the report-local port/berth override on cleanup (completion, error, or
        // unmount) so the interactive tank plan is guaranteed to never be left showing a
        // report-only scope.
        setReportPortCall(null);
        setReportBerthCall(null);
    }, [clearTimersAndObserver, setReportPortCall, setReportBerthCall]);

    const captureTankPlanImage = async (element: HTMLElement): Promise<HTMLCanvasElement | null> => {
        try {
            return await html2canvas(element, { scale: CANVAS_SCALE_FACTOR });
        } catch (error) {
            console.error('Failed to capture tank plan image:', error);
            return null;
        }
    };

    const processNextReport = useCallback(async (): Promise<void> => {
        // Re-acquire element from DOM to handle lazy-loaded components
        const element = tankPlanElementRef.current ?? document.getElementById(TANK_PLAN_CONTAINER_ID);
        if (element) {
            tankPlanElementRef.current = element;
        }
        const reports = reportsRef.current;
        const currentIndex = currentIndexRef.current;

        // Clear any potentially running fallback timer for this specific processing step,
        // as processNextReport is now actively handling it.
        if (ensureProcessingTimeoutRef.current !== null) {
            window.clearTimeout(ensureProcessingTimeoutRef.current);
            ensureProcessingTimeoutRef.current = null;
        }

        if (!element || currentIndex >= reports.length) {
            cleanup();
            setIsGenerating(false);
            return;
        }

        // Guard against capturing a stale render: confirm the DOM has actually re-rendered to
        // reflect *this* report's condition/port/berth scope before taking the screenshot. A
        // MutationObserver-triggered "stability" timer can fire on an unrelated DOM mutation
        // (e.g. lazy-loaded assets, animations) while the tank plan is still showing the
        // previous report in the queue's scope, which would otherwise capture the wrong berth's
        // content under the correct report's title. If the element has no signature attribute at
        // all (e.g. a test harness or an older/alternate render target), skip the check entirely
        // rather than blocking capture indefinitely.
        const expectedSignature = getExpectedReportSignature(reports[currentIndex]);
        const actualSignature = element.getAttribute('data-report-signature');
        const signatureMismatch = actualSignature !== null && actualSignature !== expectedSignature;
        if (signatureMismatch && signatureRecheckAttemptsRef.current < SIGNATURE_RECHECK_MAX_ATTEMPTS) {
            signatureRecheckAttemptsRef.current += 1;
            signatureRecheckTimeoutRef.current = window.setTimeout(() => {
                signatureRecheckTimeoutRef.current = null;
                processNextReport();
            }, SIGNATURE_RECHECK_INTERVAL);
            return;
        }
        if (signatureMismatch) {
            console.warn(
                `Tank plan report signature mismatch persisted after ${SIGNATURE_RECHECK_MAX_ATTEMPTS} retries ` +
                    `(expected "${expectedSignature}", got "${actualSignature}"). Capturing anyway to avoid stalling generation.`
            );
        }
        signatureRecheckAttemptsRef.current = 0;

        try {
            const image = await captureTankPlanImage(element);

            if (!image) {
                onError?.('Failed to capture tank plan image. Please try again.');
            } else if (image) {
                const base64Image = image.toDataURL('image/jpeg', JPEG_QUALITY);

                const newReport = {
                    reportSubType: reports[currentIndex].reportSubType,
                    baseData: {
                        ...reports[currentIndex].baseData,
                        imgData: base64Image,
                        imgWidth: image.width,
                        imgHeight: image.height,
                    },
                    options: reports[currentIndex].options,
                };

                setGeneratedReports((prev) => [...prev, newReport]);
                updateProgressMessage(reports[currentIndex].reportSubType, currentIndex + 1, totalReportNumberRef.current);
            }

            // Move to next report
            currentIndexRef.current = currentIndex + 1;

            // If there are more reports, set the next condition and wait for re-render
            if (currentIndex + 1 < reports.length) {
                const nextReport = reports[currentIndex + 1];
                setReportTankPlanCondition(nextReport.reportSubType.tankPlanCondition);
                setReportPortCall(nextReport.baseData.selectedPortCall ?? null);
                setReportBerthCall(nextReport.baseData.selectedBerthCall ?? null);

                // Set up a fallback timer to ensure the next report (at new currentIndexRef.current)
                // is processed if the MutationObserver doesn't detect changes for it.
                // This will be cleared by the observer if a mutation *is* detected.
                ensureProcessingTimeoutRef.current = window.setTimeout(() => {
                    console.warn(
                        `Fallback: Triggering processing for report index ${currentIndexRef.current} due to no mutation after condition set.`
                    );
                    processNextReport();
                }, FALLBACK_PROCESS_DELAY);
            } else {
                // We're done with all reports
                cleanup();
                setIsGenerating(false);
            }
        } catch (error) {
            console.error('Error processing report:', error);
            onError?.(`Error generating tank plan report: ${error instanceof Error ? error.message : 'Unknown error'}`);
            cleanup();
            setIsGenerating(false);
        }
    }, [cleanup, setReportTankPlanCondition, setReportPortCall, setReportBerthCall, updateProgressMessage]);

    const setupObserver = useCallback(() => {
        // Clean up any existing observer/timers WITHOUT resetting the report-local port/berth
        // override — that override was just set (moments ago) by generateTankPlanReports for the
        // report we're about to capture, and clearing it here would force the tank plan back to
        // whole-port scope mid-capture (this was the root cause of berth-scoped tank plan reports
        // occasionally rendering all cargo in the port instead of just the selected berth's).
        clearTimersAndObserver();

        // Re-acquire element from DOM to handle lazy-loaded components
        if (!tankPlanElementRef.current) {
            const el = document.getElementById(TANK_PLAN_CONTAINER_ID);
            if (el) {
                tankPlanElementRef.current = el;
            }
        }

        if (!tankPlanElementRef.current) return;

        // Create a new observer to detect when the tank plan has stabilized
        const observer = new MutationObserver(() => {
            // Clear any existing timer for stability
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
            }
            // A mutation was detected, so clear the fallback timer as the observer is handling it.
            if (ensureProcessingTimeoutRef.current !== null) {
                window.clearTimeout(ensureProcessingTimeoutRef.current);
                ensureProcessingTimeoutRef.current = null;
            }

            // Wait for stability before capturing
            timeoutRef.current = window.setTimeout(() => {
                processNextReport();
                timeoutRef.current = null;
            }, STABILITY_TIMEOUT);
        });

        observer.observe(tankPlanElementRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true,
        });

        observerRef.current = observer;
    }, [clearTimersAndObserver, processNextReport]);

    const generateTankPlanReports = useCallback(
        (tankPlanElement: HTMLElement, reports: TankPlanReportGenerationData[], totalNumberOfReports: number) => {
            if (reports.length === 0) return;

            cleanup();
            setGeneratedReports([]);
            setIsGenerating(true);
            totalReportNumberRef.current = totalNumberOfReports;

            tankPlanElementRef.current = tankPlanElement;
            reportsRef.current = reports;
            currentIndexRef.current = 0;

            const firstReport = reports[0];
            setReportTankPlanCondition(firstReport.reportSubType.tankPlanCondition);
            setReportPortCall(firstReport.baseData.selectedPortCall ?? null);
            setReportBerthCall(firstReport.baseData.selectedBerthCall ?? null);
            updateProgressMessage(firstReport.reportSubType, 1, totalNumberOfReports);

            // Wait for the tank plan container element to appear in the DOM
            // (it's lazy-loaded and may not be available immediately)
            const waitForElementAndStart = (attempt = 0) => {
                const el = document.getElementById(TANK_PLAN_CONTAINER_ID);
                if (el) {
                    tankPlanElementRef.current = el;
                    // Re-setup observer now that the element is available
                    setupObserver();

                    // Kick off initial processing via fallback timer.
                    // The observer will handle it if mutations occur from the initial setReportTankPlanCondition.
                    if (ensureProcessingTimeoutRef.current !== null) {
                        window.clearTimeout(ensureProcessingTimeoutRef.current);
                    }
                    ensureProcessingTimeoutRef.current = window.setTimeout(() => {
                        if (currentIndexRef.current === 0 && reportsRef.current.length > 0) {
                            console.warn(
                                `Fallback: Triggering processing for initial report (index ${currentIndexRef.current}) due to no mutation after initial condition set.`
                            );
                            processNextReport();
                        }
                    }, FALLBACK_PROCESS_DELAY);
                } else if (attempt < ELEMENT_POLL_MAX_ATTEMPTS) {
                    // Element not yet in DOM (lazy component loading) — retry
                    window.setTimeout(() => waitForElementAndStart(attempt + 1), ELEMENT_POLL_INTERVAL);
                } else {
                    console.error('Tank plan report container element not found after waiting. Aborting report generation.');
                    onError?.('Tank plan report container not found. Please try again.');
                    cleanup();
                    setIsGenerating(false);
                }
            };

            waitForElementAndStart();
        },
        [cleanup, setupObserver, setReportTankPlanCondition, setReportPortCall, setReportBerthCall, updateProgressMessage]
    );

    // Clean up on unmount
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    return {
        generateTankPlanReports,
        generatedReports,
        isGenerating,
    };
};
